import { useState } from 'react';
import { apiUrl } from '../lib/api';

/* ============================================================================
   Public legal pages — reachable WITHOUT signing in.

   The deletion page is not decoration: Google Play and Apple both require an
   account-deletion route a visitor can reach without an account, and India's DPDP
   Act requires an erasure path. This file provides all three public documents.

   ⚠ The text below is a solid working draft, NOT legal advice. Before signing a
   school, have a lawyer review it — this product processes children's data.
   ============================================================================ */

const UPDATED = 'July 2026';
const CONTACT = 'hello@eduvoratech.com';

/* ⚠ THE REGISTERED ENTITY, SPELLED AS ON THE UDYAM CERTIFICATE. A privacy policy that names a
   company but gives no address is the first thing an app-store reviewer, a payment gateway and
   Meta's business verification all bounce, and DPDP expects a data fiduciary a parent can actually
   write to. Keep this identical to the landing-page footer — two versions of one address is worse
   than none, because a reviewer comparing them assumes the papers are wrong. */
const ORG = {
  name: 'AIVENTRIX TECHNOLOGIES',
  address: '3619/6 & 3619/7, Matru Krupa, 4th Main, 13th Cross, SS Layout, Davanagere, Karnataka 577006, India',
  phone: '+91 88840 46487',
  udyam: 'UDYAM-KR-12-0072483',
};

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="lp-root" style={{ minHeight: '100vh' }}>
      <header style={{ padding: '22px 24px', borderBottom: '1px solid rgba(128,128,128,0.18)' }}>
        <div style={{ maxWidth: 820, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <a href="/" style={{ fontWeight: 700, fontSize: 18, textDecoration: 'none', color: 'inherit' }}>Eduvora</a>
          <nav style={{ marginLeft: 'auto', display: 'flex', gap: 16, fontSize: 13 }}>
            <a href="/terms" style={{ color: 'inherit', opacity: 0.75 }}>Terms</a>
            <a href="/privacy" style={{ color: 'inherit', opacity: 0.75 }}>Privacy</a>
            <a href="/delete-account" style={{ color: 'inherit', opacity: 0.75 }}>Delete account</a>
          </nav>
        </div>
      </header>

      <main style={{ maxWidth: 820, margin: '0 auto', padding: '40px 24px 80px' }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 6, letterSpacing: '-0.5px' }}>{title}</h1>
        <p style={{ opacity: 0.6, fontSize: 13, marginBottom: 32 }}>Last updated: {UPDATED}</p>
        <div className="legal-body" style={{ lineHeight: 1.75, fontSize: 15 }}>{children}</div>
      </main>

      <footer style={{ borderTop: '1px solid rgba(128,128,128,0.18)', padding: '24px', fontSize: 12, opacity: 0.6 }}>
        <address style={{ maxWidth: 820, margin: '0 auto', fontStyle: 'normal', lineHeight: 1.7 }}>
          Eduvora is a product of <strong>{ORG.name}</strong><br />
          {ORG.address}<br />
          {ORG.phone} · <a href={`mailto:${CONTACT}`} style={{ color: 'inherit' }}>{CONTACT}</a> · Udyam Reg. {ORG.udyam}
        </address>
      </footer>
    </div>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return <h2 style={{ fontSize: 19, fontWeight: 700, margin: '30px 0 10px' }}>{children}</h2>;
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service">
      <p>These terms govern the use of Eduvora, a school management platform operated by AIVENTRIX
        Technologies ("we", "us"). By using Eduvora, the school and its users agree to them.</p>

      <H2>1. Who our customer is</H2>
      <p>Our customer is the <strong>school or institution</strong> that subscribes to Eduvora. Staff,
        parents and students use the platform through that school's account. The school decides who gets
        access and at what level.</p>

      <H2>2. The school owns its data</H2>
      <p>All data a school enters — students, staff, fees, results — belongs to that school. We store and
        process it on the school's behalf. We do not sell it, and we do not use one school's data to serve
        another. On termination, the school may request an export of its data.</p>

      <H2>3. Separation between schools</H2>
      <p>Each school's data is isolated at the database level. A user signed in to one school cannot read
        another school's records.</p>

      <H2>4. Acceptable use</H2>
      <p>Accounts must not be shared. Users must not attempt to access records they are not entitled to,
        interfere with the service, or upload unlawful content. The school is responsible for the conduct
        of the accounts it issues.</p>

      <H2>5. Fees and payments</H2>
      <p>Where a school collects fees through Eduvora, payments are processed by a third-party payment
        gateway using the school's own merchant account. <strong>Funds move between the payer and the
        school. We do not hold, route, or take a share of fee money.</strong> Gateway charges are governed
        by the school's agreement with the gateway.</p>

      <H2>6. Availability</H2>
      <p>We aim for continuous availability but do not guarantee uninterrupted service. Planned maintenance
        will be notified where practical. We are not liable for outages caused by third parties such as
        hosting, network, or payment providers.</p>

      <H2>7. What Eduvora is not</H2>
      <p>Eduvora is a record-keeping and administration tool. It does not provide legal, financial, tax, or
        statutory advice. Where the platform supports payroll, <strong>statutory rates must be verified by
        the school's own qualified accountant before use</strong> — the system deliberately ships with none
        and refuses to issue payslips on unverified rules. Compliance with education, labour, and tax law
        remains the school's responsibility.</p>

      <H2>8. Limitation of liability</H2>
      <p>To the extent permitted by law, our total liability in any twelve-month period is limited to the
        fees paid by the school in that period. We are not liable for indirect or consequential loss.</p>

      <H2>9. Suspension and termination</H2>
      <p>Either party may terminate with notice as set out in the school's subscription agreement. We may
        suspend access for non-payment or serious misuse, after notice where practical.</p>

      <H2>10. Changes</H2>
      <p>We may update these terms. Material changes will be notified to subscribing schools. Continued use
        after a change constitutes acceptance.</p>

      <H2>11. Governing law</H2>
      <p>These terms are governed by the laws of India, with courts at Bengaluru, Karnataka having
        jurisdiction.</p>

      <H2>12. Contact</H2>
      <p>{CONTACT}</p>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <p>This policy explains what Eduvora collects, why, and what rights people have. It matters
        particularly because <strong>this platform processes data about children</strong>, and we treat
        that as the higher standard it is.</p>

      <H2>1. Our role</H2>
      <p>Under India's Digital Personal Data Protection Act, the <strong>school is the Data Fiduciary</strong>
        — it decides what data is collected and why. <strong>We are the Data Processor</strong>, acting on
        the school's instructions. If you are a parent or student, your first point of contact is your
        school; we support the school in meeting its obligations.</p>

      <H2>2. What is collected</H2>
      <ul>
        <li><strong>Students:</strong> name, admission number, class, guardians, contact details,
          attendance, fees, results, and — where the school enables those modules — transport, hostel,
          library, and store records.</li>
        <li><strong>Guardians:</strong> name, relationship, phone, email, payment records.</li>
        <li><strong>Staff:</strong> employment records, attendance, leave, and payroll data.</li>
        <li><strong>Technical:</strong> log data needed to operate and secure the service.</li>
      </ul>

      <H2>3. Children's data</H2>
      <p>Data about a child is provided and controlled by the school and the child's guardian. A child
        cannot make a data request alone — requests concerning a student must come from a verified guardian
        or the school. Where consent is required, the school records the guardian's consent, and consent
        can be withdrawn.</p>

      <H2>4. Why it is processed</H2>
      <p>Solely to deliver school administration: enrolment, attendance, fees and receipts, examinations,
        transport and safety, communication with families, and staff administration.
        <strong> We do not sell personal data, and we do not use it for advertising.</strong></p>

      <H2>5. Location tracking</H2>
      <p>Where a school enables live bus tracking, the tracked device is the <strong>driver's phone, not any
        child</strong>. Parents can see a bus only while a trip is running, and only for a route their child
        is assigned to. Position data is not retained after the trip ends.</p>

      <H2>6. Sharing</H2>
      <p>We share data only with service providers necessary to run the platform — hosting, email/SMS
        delivery, and payment processing — each bound to process it only for that purpose. We disclose data
        to authorities only where legally required.</p>

      <H2>7. Security</H2>
      <p>Data is transmitted over encrypted connections and isolated per school at the database level.
        Access is role-based: a parent can only ever see their own child's records, enforced on every
        request. Support access to a school's account is read-only, time-limited, requires a stated reason,
        and is visible to the school.</p>

      <H2>8. Retention</H2>
      <p>Data is retained while the school's subscription is active and afterwards only as long as the
        school's own statutory obligations require — for example, academic records a school must keep to
        issue transcripts. When a record is erased, we record what was removed and what was lawfully kept,
        and why.</p>

      <H2>9. Rights</H2>
      <p>Individuals may request access to, correction of, or erasure of their data, and may withdraw
        consent. Requests should be made to the school; where you cannot reach the school, contact us and
        we will route it. See <a href="/delete-account">Delete account</a> for erasure.</p>
      <p>Erasure is not always absolute: a school may be legally required to retain certain records. In
        that case the school must tell you what was kept and on what basis.</p>

      <H2>10. Grievances</H2>
      <p>Concerns can be raised with the school's designated contact, or with us at {CONTACT}. We aim to
        respond within a reasonable time as required by law.</p>

      <H2>11. Changes</H2>
      <p>We will notify schools of material changes to this policy.</p>
    </LegalShell>
  );
}

export function DeleteAccountPage() {
  const [form, setForm] = useState({ requesterName: '', contactEmail: '', contactPhone: '', schoolName: '', subjectDescription: '', reason: '' });
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState('');

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.requesterName.trim() || !form.contactEmail.trim()) {
      setState('error'); setMsg('Please give your name and an email address so we can verify the request.');
      return;
    }
    setState('sending'); setMsg('');
    try {
      const res = await fetch(apiUrl('/api/public/legal/deletion-request'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requesterName: form.requesterName.trim(),
          contactEmail: form.contactEmail.trim(),
          contactPhone: form.contactPhone.trim() || null,
          schoolName: form.schoolName.trim() || null,
          subjectDescription: form.subjectDescription.trim() || null,
          reason: form.reason.trim() || null,
        }),
      });
      if (!res.ok) throw new Error();
      setState('done');
    } catch {
      setState('error');
      setMsg(`Could not submit. Please email ${CONTACT} directly and we will action it.`);
    }
  };

  const field: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14,
    border: '1px solid rgba(128,128,128,0.35)', background: 'transparent', color: 'inherit', marginTop: 4,
  };
  const label: React.CSSProperties = { display: 'block', fontSize: 13, opacity: 0.75, marginTop: 16 };

  return (
    <LegalShell title="Delete your account and data">
      {state === 'done' ? (
        <div style={{ padding: '20px 22px', border: '1px solid rgba(26,127,69,0.4)', borderRadius: 12, background: 'rgba(26,127,69,0.08)' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Request received</h2>
          <p>We have recorded your request. <strong>Nothing has been deleted yet</strong> — we will contact
            you at the email you gave to verify your identity first.</p>
          <p style={{ marginTop: 10 }}>This step protects students: we will not act on an unverified request
            to erase a child's records. You should hear from us, or your school, shortly.</p>
        </div>
      ) : (
        <>
          <p>You can ask for your Eduvora account and personal data to be deleted. This page is open to
            everyone — you do not need to be signed in.</p>

          <H2>What happens when you ask</H2>
          <ol style={{ paddingLeft: 20 }}>
            <li><strong>We verify who you are.</strong> Because Eduvora holds records about children, we
              will not erase anything on an unverified request. We will contact you at the email below.</li>
            <li><strong>Your school is involved.</strong> Your school is the owner of its records, so the
              request is actioned with them.</li>
            <li><strong>We tell you what was removed — and what was kept.</strong> Schools are sometimes
              legally required to retain certain records (for example, academic records needed to issue a
              transcript). If anything is retained, you will be told what, and on what legal basis.</li>
          </ol>

          <H2>What gets deleted</H2>
          <p>Your login and profile, and personal data held for you or your child, except records the school
            must lawfully retain. Deletion is permanent and cannot be undone.</p>

          <H2>Request deletion</H2>
          <p style={{ fontSize: 13, opacity: 0.75 }}>You can also email {CONTACT} instead of using this form.</p>

          <div style={{ marginTop: 8 }}>
            <label style={label}>Your full name <span style={{ color: '#c1272d' }}>*</span>
              <input style={field} value={form.requesterName} onChange={set('requesterName')} />
            </label>
            <label style={label}>Your email <span style={{ color: '#c1272d' }}>*</span>
              <input style={field} type="email" value={form.contactEmail} onChange={set('contactEmail')} />
            </label>
            <label style={label}>Phone (optional)
              <input style={field} value={form.contactPhone} onChange={set('contactPhone')} />
            </label>
            <label style={label}>Your school's name
              <input style={field} value={form.schoolName} onChange={set('schoolName')} placeholder="Helps us find the right records" />
            </label>
            <label style={label}>Whose data is this?
              <input style={field} value={form.subjectDescription} onChange={set('subjectDescription')} placeholder="My own account / my child, class 4B" />
            </label>
            <label style={label}>Reason (optional)
              <textarea style={{ ...field, minHeight: 80, resize: 'vertical' }} value={form.reason} onChange={set('reason')} />
            </label>

            {state === 'error' && <p style={{ color: '#c1272d', fontSize: 13, marginTop: 12 }}>{msg}</p>}

            <button onClick={submit} disabled={state === 'sending'}
              style={{ marginTop: 22, padding: '11px 22px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: '#0f5ea8', color: '#fff', fontSize: 14, fontWeight: 600 }}>
              {state === 'sending' ? 'Submitting…' : 'Submit deletion request'}
            </button>
          </div>
        </>
      )}
    </LegalShell>
  );
}
